import Input from "../../../components/input";
import Button from "../../../components/button";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { apiEndPoints } from "../../../hooks/remote/apiEndPoints";
import { toast } from "react-toastify";

const ResetPassword = () => {
  document.title =
    "Create New Password - CloudVantage";

  const location = useLocation();
  const navigate = useNavigate();

  const email =
    location.state?.email || "";

  const otp =
    location.state?.otp || "";

  const [loading, setLoading] =
    useState(false);

  /*
   * Do not allow direct access.
   * User must verify OTP first.
   */
  useEffect(() => {
    if (!email || !otp) {
      toast.error(
        "Please verify your reset code first."
      );

      navigate(
        "/verify-reset-otp",
        {
          replace: true,
        }
      );
    }
  }, [email, otp, navigate]);

  const createNewPasswordForm =
    useFormik({
      initialValues: {
        new_password: "",
        confirm_new_password: "",
      },

      validationSchema: Yup.object({
        new_password: Yup.string()
          .required(
            "Password cannot be empty"
          )
          .min(
            12,
            "Password must be at least 12 characters long"
          )
          .matches(
            /[A-Z]/,
            "Password must contain at least one uppercase letter"
          )
          .matches(
            /[a-z]/,
            "Password must contain at least one lowercase letter"
          )
          .matches(
            /[0-9]/,
            "Password must contain at least one digit"
          )
          .matches(
            /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/,
            "Password must contain at least one special character"
          ),

        confirm_new_password:
          Yup.string()
            .oneOf(
              [
                Yup.ref(
                  "new_password"
                ),
                null,
              ],
              "Passwords must match"
            )
            .required(
              "Please confirm your password"
            ),
      }),

      onSubmit: async (
        values,
        { resetForm }
      ) => {
        try {
          setLoading(true);

          const response =
            await apiEndPoints.setNewPassword({
              email,
              new_password:
                values.new_password,
              otp,
            });

          const data =
            response?.data?.data ||
            response?.data ||
            response;

          if (
            data?.statuscode &&
            data.statuscode !== "00"
          ) {
            toast.error(
              data?.statusmessage ||
                data?.message ||
                "Unable to reset password."
            );

            return;
          }

          toast.success(
            data?.statusmessage ||
              "Password reset successfully."
          );

          resetForm();

          navigate("/sign-in", {
            replace: true,
          });
        } catch (error) {
          toast.error(
            error?.response?.data
              ?.statusmessage ||
              error?.response?.data
                ?.message ||
              error?.message ||
              "Unable to reset password."
          );
        } finally {
          setLoading(false);
        }
      },
    });

  if (!email || !otp) {
    return null;
  }

  return (
    <div className="px-4 flex justify-center items-center py-8">
      <div className="my-3 w-full md:w-[50%] lg:w-[40%]">

        <form
          onSubmit={
            createNewPasswordForm.handleSubmit
          }
        >
          <div className="bg-white dark:bg-[#141D26] border border-[#c4c4c432] dark:text-white rounded-md px-6 py-10 grid gap-6">

            <div>
              <p className="pb-1 crimson-font text-lg font-semibold">
                Create new password
              </p>

              <p className="text-xs text-[#c4c4c4] pb-2 border-b dark:border-b-[#c4c4c432]">
                Enter a new password for your
                account.
              </p>
            </div>

            <Input
              label="New password"
              type="password"
              isPassword="true"
              name="new_password"
              value={
                createNewPasswordForm
                  .values.new_password
              }
              onBlur={
                createNewPasswordForm
                  .handleBlur
              }
              onChange={
                createNewPasswordForm
                  .handleChange
              }
              onError={
                createNewPasswordForm
                  .touched.new_password &&
                createNewPasswordForm
                  .errors.new_password
              }
            />

            <Input
              label="Confirm password"
              type="password"
              isPassword="true"
              name="confirm_new_password"
              value={
                createNewPasswordForm
                  .values
                  .confirm_new_password
              }
              onBlur={
                createNewPasswordForm
                  .handleBlur
              }
              onChange={
                createNewPasswordForm
                  .handleChange
              }
              onError={
                createNewPasswordForm
                  .touched
                  .confirm_new_password &&
                createNewPasswordForm
                  .errors
                  .confirm_new_password
              }
            />

            <div className="text-xs text-gray-500 dark:text-gray-400">
              <p className="mb-2">
                Your password must contain:
              </p>

              <ul className="list-disc list-inside space-y-1">
                <li>
                  At least 12 characters
                </li>
                <li>
                  At least 1 uppercase letter
                </li>
                <li>
                  At least 1 lowercase letter
                </li>
                <li>
                  At least 1 number
                </li>
                <li>
                  At least 1 special character
                </li>
              </ul>
            </div>

            <Button
              buttonRole="submit"
              loading={loading}
              buttonText="Reset password"
            />

          </div>
        </form>

      </div>
    </div>
  );
};

export default ResetPassword;